import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { isNumber } from 'lodash';

import {
    SessionSlot,
    StringifiedSessionSlot,
} from 'src/modules/videoSessions/models/videoSessions';
import { DeeplinkedSessionReview } from 'src/modules/payments/components/DeeplinkedSessionReview';
import { DirectSessionReview } from 'src/modules/payments/components/DirectSessionReview';
import {
    getTherapySessions,
    getVideoSessions,
    isSessionFetchingCompleted,
} from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import { CoachingVisitState, CostEstimation } from 'src/generated/webapp_gql';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import moment from 'moment';
import { ProviderType } from 'src/modules/user/models/user';

type LocationState = {
    providerUuid?: string;
    session?: string;
    sessionSlot?: StringifiedSessionSlot;
    costEstimation?: CostEstimation;
    onboardingMode?: boolean;
    isSuccess?: boolean;
} & Record<string, unknown>;

type Props = RouteComponentProps<{}, {}, LocationState>;

export const PaymentsSessionReviewPage: FC<Props> = ({ location }) => {
    const sessionUuid = useMemo(() => {
        if (location.search) {
            const searchParams = new URLSearchParams(window.location.search);

            if (searchParams.get('session')) {
                return searchParams.get('session');
            }
        }

        return location.state?.session;
    }, [location]);

    const isSuccess = location.state?.isSuccess;
    const sessions = useSelector(getVideoSessions);
    const pammSession = sessions?.find(session => session.uuid === sessionUuid);
    const isPamm = pammSession?.coachAssignment?.coach?.providerType === ProviderType.PSYCHIATRIST;
    const therapySessions = useSelector(getTherapySessions);
    const coachingSessionsFetchCompleted = useSelector(isSessionFetchingCompleted);
    const therapySession = therapySessions.find(session => session.uuid === sessionUuid);

    const session = isPamm ? pammSession : therapySession;

    if (!coachingSessionsFetchCompleted) {
        return <CenteredSpinner />;
    }

    if (!session) {
        return <Redirect to="/dashboard" />;
    }

    if (
        !(
            session &&
            ([CoachingVisitState.PendingPayment, CoachingVisitState.PendingConfirmation].includes(
                session.state,
            ) ||
                (session.state === CoachingVisitState.Scheduled && isSuccess))
        )
    ) {
        return <Redirect to="/dashboard" />;
    }

    if (
        isNumber(location.state?.costEstimation?.patientResponsibility) &&
        location.state?.sessionSlot
    ) {
        const standardizedSessionSlot: SessionSlot = {
            start: moment.utc(location.state.sessionSlot.start).local(),
            end: moment.utc(location.state.sessionSlot.end).local(),
        };
        return (
            <DirectSessionReview
                {...{
                    ...location.state,
                    session,
                    sessionSlot: standardizedSessionSlot,
                }}
            />
        );
    }

    return <DeeplinkedSessionReview session={session} isSuccess={isSuccess} />;
};
